import React, { useState } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

interface EditPricePopoverProps {
  price: number;
  originalPrice: number;
  onPriceChange: (newPrice: number) => void;
  priceGuardPercentage?: number;
}

export function EditPricePopover({ price, originalPrice, onPriceChange, priceGuardPercentage }: EditPricePopoverProps) {
  const currencySymbol = useCurrencySymbol();
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState(price.toString());

  const guardPercent = typeof priceGuardPercentage === 'number' && Number.isFinite(priceGuardPercentage)
    ? Math.max(0, priceGuardPercentage)
    : 500;
  const factor = guardPercent / 100;
  const minPrice = Math.max(0, originalPrice * (1 - factor));
  const maxPrice = originalPrice * (1 + factor);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseFloat(value);
    if (isNaN(parsed) || parsed < 0) {
      toast.error('Price must be a positive number');
      return;
    }
    if (parsed < minPrice || parsed > maxPrice) {
      toast.error(`Price must stay within ${guardPercent}% of the catalog price (${currencySymbol}${minPrice.toFixed(2)} - ${currencySymbol}${maxPrice.toFixed(2)})`);
      return;
    }
    onPriceChange(parsed);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-6 w-6">
          <Edit2 className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64">
        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="space-y-1">
            <label htmlFor="edit-price" className="text-sm font-medium">
              Edit Price ({currencySymbol}{minPrice.toFixed(2)} - {currencySymbol}{maxPrice.toFixed(2)})
            </label>
            <Input
              id="edit-price"
              type="number"
              step="0.01"
              min="0"
              value={value}
              onChange={e => setValue(e.target.value)}
              required
            />
          </div>
          <Button type="submit" size="sm" className="w-full">
            Update Price
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}
